import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'utils/axiosUtil';
import { DateUtil } from 'doctivity-shared/utils';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DynamicTable } from 'components';
import { notificationTarget } from 'components/NotificationView';

const PAGE_SIZE = 25;

/**
 * The full notification inbox — everything the bell popover can't show at 360px.
 *
 * Built on DynamicTable so it reads like every other list in the app (same header,
 * same pager, same row-click-to-open behaviour) rather than inventing a one-off
 * layout. Rows link to /notifications/:id via getRowHref, so a permalink can be
 * middle-clicked or opened in a new tab like any other record in the site.
 *
 * Paged rather than "load everything": recipient rows only accumulate (one per
 * system-wide announcement, one per note/mileage assignment), so an unbounded
 * fetch would grow with the age of the account.
 */
const NotificationsPage = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  // DynamicTable is controlled: it hands back a query with the new limit/offset and
  // we refetch. Sorting and filtering are off (see columns) because the API orders
  // by newest-first and takes no other criteria — better to not offer a control
  // than to offer one the server ignores.
  const [query, setQuery] = useState({ limit: PAGE_SIZE, offset: 0 });

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/v2/my/notifications', {
        params: { limit: q.limit, offset: q.offset },
      });
      setRows(Array.isArray(data?.notifications) ? data.notifications : []);
      setCount(data?.count || 0);
      setUnread(data?.unread_count || 0);
    } catch (e) {
      setRows([]);
      setCount(0);
      setUnread(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(query);
  }, [load, query]);

  // Rows that link away to a note/mileage log never reach NotificationPage, so
  // nothing else would ever mark them read. Fire-and-forget, then grey the row
  // locally so it doesn't still look unread if the user comes straight back.
  const markReadOnLeave = (row) => {
    if (!row || row.read_at || notificationTarget(row) === row.permalink) return;
    setRows((list) => list.map((r) => (
      r.id === row.id ? { ...r, read_at: new Date().toISOString() } : r
    )));
    setUnread((n) => Math.max(0, n - 1));
    axiosInstance.post(`/v2/my/notifications/${row.id}/read`).catch(() => {});
  };

  const handleDelete = async (row) => {
    try {
      await axiosInstance.delete(`/v2/my/notifications/${row.id}`);
    } catch (e) {
      /* non-fatal */
    }
    // Refetch rather than splicing locally, so the pager's count and the page
    // contents stay consistent when a row is removed mid-page.
    load(query);
  };

  const columns = [
    {
      label: 'Notification',
      key: 'title',
      sortable: false,
      filter: false,
      style: { minWidth: 240 },
      format: (v, row) => (
        <Typography variant='body2' sx={{ fontWeight: row && row.read_at ? 400 : 700 }}>
          {v}
        </Typography>
      ),
    },
    { label: 'From', key: 'sender_name', sortable: false, filter: false },
    {
      label: 'Received',
      key: 'created_at',
      sortable: false,
      filter: false,
      format: (v) => (v ? DateUtil.formatFromDB(v) : ''),
    },
    {
      label: 'Status',
      key: 'read_at',
      sortable: false,
      filter: false,
      format: (v) => (
        <Chip size='small' label={v ? 'Read' : 'Unread'} color={v ? 'default' : 'error'} />
      ),
    },
    {
      label: '',
      key: 'id',
      sortable: false,
      filter: false,
      style: { width: 48 },
      format: (v, row) => (
        <IconButton
          size='small'
          aria-label='Delete'
          title='Remove from your notifications'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDelete(row);
          }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      ),
    },
  ];

  return (
    // Same shape as ProviderCommentsPage (the Activity Notes list): the page's
    // green header bar lives in NotificationsPageContainer, and the card below it
    // carries a green section title, then the table with ContainerType=Fragment so
    // DynamicTable doesn't add a second Paper inside this Card.
    <Box sx={{ px: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
            <Typography component='h2' variant='h6' color='primary'>
              All Notifications
            </Typography>
            <Box sx={{ flex: 1 }} />
            {unread > 0 && (
              <Typography variant='body2' color='textSecondary'>
                {unread} unread
              </Typography>
            )}
          </Box>

          <DynamicTable
            showFilters={false}
            ContainerType={React.Fragment}
            columns={columns}
            idKey='id'
            query={query}
            data={{ rows, count, loading }}
            onQueryChange={setQuery}
            // getRowHref does the navigating — DynamicTable wraps each cell in a
            // router <Link to={href}>, which also makes rows middle-clickable. A
            // note assignment points at the note itself, not at a page about it.
            // The action buttons call preventDefault() because they live inside
            // that Link.
            getRowHref={notificationTarget}
            // Not navigation — the Link above handles that. This only marks a row
            // read when the click leaves for a note, since that path never reaches
            // NotificationPage, which is what normally marks it read.
            onRowClick={markReadOnLeave}
            noDataMessage="You're all caught up."
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export { NotificationsPage };
export default NotificationsPage;
